import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Pencil,
  Trash2,
  RefreshCw,
  Save,
  X,
  CreditCard,
  Package,
  Users,
  CheckCircle2,
  XCircle,
  PlusCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Mail,
  FileSignature,
  Cpu,
  Building2,
  LucideIcon,
} from 'lucide-react'
import api from '../../services/api'

// ── Types ─────────────────────────────────────────────────────────────────────

interface SubscriptionPlan {
  id: number
  name: string
  description: string
  description_fr: string
  description_en: string
  base_price: number
  max_users: number
  max_clients: number
  max_signatures: number
  max_ai_tokens: number
  max_emails: number
  max_sku_free: number
  features: string[]
  is_custom: boolean
  is_active: boolean
}

interface CreditCost {
  resource_type: string
  description: string
  credits_per_unit: number
  units_per_credit: number
  is_active: boolean
}

interface ErpBillingRow {
  erp_id: number
  erp_name: string
  erp_type: string
  subscription: {
    plan_name: string
    status: string
    next_billing_date: string | null
    credit_balance: number
    monthly_allowance: number
    is_active: boolean
    is_custom: boolean
    quantity: number
    auto_recharge_enabled: boolean
    effective_max_users: number
    effective_max_clients: number
    effective_max_signatures: number
    effective_max_ai_tokens: number
    effective_max_emails: number
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number): string {
  return n.toLocaleString('fr-FR')
}

function StatusBadge({ active }: { active: boolean }) {
  const { t } = useTranslation()
  return active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <CheckCircle2 className="w-3 h-3" /> {t('adminBilling.status.active')}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      <XCircle className="w-3 h-3" /> {t('adminBilling.status.inactive')}
    </span>
  )
}

function QuotaGrid({
  plan,
  qty = 1,
  custom = false,
}: {
  plan: Pick<
    SubscriptionPlan,
    'max_users' | 'max_clients' | 'max_signatures' | 'max_ai_tokens' | 'max_emails' | 'max_sku_free'
  >
  qty?: number
  custom?: boolean
}) {
  const { t } = useTranslation()
  const rows: { icon: LucideIcon; label: string; value: number }[] = [
    { icon: Users, label: t('adminBilling.quota.users'), value: plan.max_users * qty },
    { icon: Building2, label: t('adminBilling.quota.clients'), value: plan.max_clients * qty },
    {
      icon: FileSignature,
      label: t('adminBilling.quota.signatures'),
      value: plan.max_signatures * qty,
    },
    { icon: Cpu, label: t('adminBilling.quota.aiTokens'), value: plan.max_ai_tokens * qty },
    { icon: Mail, label: t('adminBilling.quota.emails'), value: plan.max_emails * qty },
    {
      icon: Package,
      label: t('adminBilling.quota.catalogProducts'),
      value: plan.max_sku_free * qty,
    },
  ]
  return (
    <ul className="space-y-1 text-sm text-gray-600 dark:text-gray-300">
      {rows.map(({ icon: Icon, label, value }) => (
        <li key={label} className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5">
            <Icon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" /> {label}
          </span>
          <span className="font-medium">
            {custom ? t('adminBilling.quota.custom') : fmt(value)}
          </span>
        </li>
      ))}
    </ul>
  )
}

// ── Plan form modal ───────────────────────────────────────────────────────────

interface PlanFormProps {
  plan: Partial<SubscriptionPlan> | null
  onClose: () => void
  onSaved: () => void
}

function PlanForm({ plan, onClose, onSaved }: PlanFormProps) {
  const { t } = useTranslation()
  const isNew = !plan?.id
  const [form, setForm] = useState({
    id: plan?.id,
    name: plan?.name ?? '',
    description_fr: plan?.description_fr ?? plan?.description ?? '',
    description_en: plan?.description_en ?? '',
    base_price: plan?.base_price ?? 0,
    max_users: plan?.max_users ?? 0,
    max_clients: plan?.max_clients ?? 0,
    max_signatures: plan?.max_signatures ?? 0,
    max_ai_tokens: plan?.max_ai_tokens ?? 0,
    max_emails: plan?.max_emails ?? 0,
    max_sku_free: plan?.max_sku_free ?? 0,
    is_custom: plan?.is_custom ?? false,
    features: (plan?.features ?? []).join('\n'),
    is_active: plan?.is_active ?? true,
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    if (!form.name.trim()) {
      setError(t('adminBilling.planForm.nameRequired'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        ...form,
        features: form.features
          .split('\n')
          .map((f: string) => f.trim())
          .filter(Boolean),
      }
      const res = await api.saveSubscriptionPlan(payload)
      if (!res.success) throw new Error(res.message)
      onSaved()
    } catch (err: unknown) {
      setError((err as Error)?.message ?? t('adminBilling.planForm.savingError'))
    } finally {
      setSaving(false)
    }
  }

  const Field = ({
    label,
    field,
    type = 'number',
    step,
    placeholder,
  }: {
    label: string
    field: keyof typeof form
    type?: string
    step?: number
    placeholder?: string
  }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
        {label}
      </label>
      <input
        type={type}
        min={type === 'number' ? 0 : undefined}
        step={step}
        disabled={
          form.is_custom &&
          field !== 'name' &&
          field !== 'description_fr' &&
          field !== 'description_en' &&
          field !== 'is_custom' &&
          field !== 'is_active' &&
          field !== 'features'
        }
        placeholder={placeholder}
        value={form[field] as string | number}
        onChange={(e) =>
          setForm((f) => ({
            ...f,
            [field]: type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value,
          }))
        }
        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-40"
      />
    </div>
  )

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-lg my-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            {isNew
              ? t('adminBilling.planForm.titleNew')
              : t('adminBilling.planForm.titleEdit', { name: plan?.name })}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('adminBilling.planForm.name')}
              </label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Starter, Pro, Gold, Entreprise…"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('adminBilling.planForm.descriptionFr')}
              </label>
              <textarea
                value={form.description_fr}
                onChange={(e) => setForm((f) => ({ ...f, description_fr: e.target.value }))}
                rows={2}
                placeholder="Description en français…"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('adminBilling.planForm.descriptionEn')}
              </label>
              <textarea
                value={form.description_en}
                onChange={(e) => setForm((f) => ({ ...f, description_en: e.target.value }))}
                rows={2}
                placeholder="English description…"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
              />
            </div>
          </div>
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={form.is_custom}
              onChange={(e) => setForm((f) => ({ ...f, is_custom: e.target.checked }))}
              className="w-4 h-4 accent-purple-600"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300 font-medium">
              {t('adminBilling.planForm.customPlan')}
            </span>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t('adminBilling.planForm.priceLabel')} field="base_price" step={0.01} />
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('adminBilling.planForm.statusLabel')}
              </label>
              <select
                value={form.is_active ? '1' : '0'}
                onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.value === '1' }))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="1">{t('adminBilling.planForm.statusActive')}</option>
                <option value="0">{t('adminBilling.planForm.statusInactive')}</option>
              </select>
            </div>
            <Field label={t('adminBilling.planForm.usersPerPack')} field="max_users" />
            <Field label={t('adminBilling.planForm.clientsPerPack')} field="max_clients" />
            <Field label={t('adminBilling.planForm.signaturesPerPack')} field="max_signatures" />
            <Field label={t('adminBilling.planForm.aiTokensPerPack')} field="max_ai_tokens" />
            <Field label={t('adminBilling.planForm.emailsPerPack')} field="max_emails" />
            <Field label={t('adminBilling.planForm.catalogPerPack')} field="max_sku_free" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adminBilling.planForm.featuresLabel')}{' '}
              <span className="text-gray-400 font-normal">
                {t('adminBilling.planForm.featuresHint')}
              </span>
            </label>
            <textarea
              value={form.features}
              onChange={(e) => setForm((f) => ({ ...f, features: e.target.value }))}
              rows={3}
              placeholder={t('adminBilling.planForm.featuresPlaceholder')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none font-mono"
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
          >
            {t('adminBilling.planForm.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {isNew ? t('adminBilling.planForm.create') : t('adminBilling.planForm.save')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Add credits modal ─────────────────────────────────────────────────────────

function AddCreditsModal({
  erp,
  onClose,
  onAdded,
}: {
  erp: ErpBillingRow
  onClose: () => void
  onAdded: () => void
}) {
  const { t } = useTranslation()
  const [amount, setAmount] = useState('')
  const [description, setDescription] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async () => {
    const amt = parseFloat(amount)
    if (!amt || amt <= 0) {
      setError(t('adminBilling.addCredits.invalidAmount'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await api.addErpCredits(erp.erp_id, amt, description || undefined)
      if (!res.success) throw new Error(res.message)
      onAdded()
    } catch (e: unknown) {
      setError((e as Error)?.message ?? t('adminBilling.addCredits.genericError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {t('adminBilling.addCredits.title', { name: erp.erp_name })}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adminBilling.addCredits.amountLabel')}
            </label>
            <input
              type="number"
              min={0.01}
              step={0.01}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="50.00"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adminBilling.addCredits.reasonLabel')}
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder={t('adminBilling.addCredits.reasonPlaceholder')}
            />
          </div>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
          >
            {t('adminBilling.addCredits.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <PlusCircle className="w-4 h-4" />
            )}{' '}
            {t('adminBilling.addCredits.add')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Assign plan modal ─────────────────────────────────────────────────────────

function AssignPlanModal({
  erp,
  plans,
  onClose,
  onAssigned,
}: {
  erp: ErpBillingRow
  plans: SubscriptionPlan[]
  onClose: () => void
  onAssigned: () => void
}) {
  const { t } = useTranslation()
  const [planId, setPlanId] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selectedPlan = plans.find((p) => String(p.id) === planId) ?? null

  const handleSubmit = async () => {
    if (!planId) {
      setError(t('adminBilling.assignPlan.choosePlanRequired'))
      return
    }
    if (quantity < 1) {
      setError(t('adminBilling.assignPlan.quantityMin'))
      return
    }
    setSaving(true)
    setError(null)
    try {
      const res = await api.activateErpSubscription(erp.erp_id, parseInt(planId), quantity)
      if (!res.success) throw new Error(res.message)
      onAssigned()
    } catch (e: unknown) {
      setError((e as Error)?.message ?? t('adminBilling.assignPlan.genericError'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-sm">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white">
            {t('adminBilling.assignPlan.title', { name: erp.erp_name })}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="w-4 h-4 text-gray-500" />
          </button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('adminBilling.assignPlan.planLabel')}
            </label>
            <select
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">{t('adminBilling.assignPlan.choosePlanOption')}</option>
              {plans.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                  {p.is_custom
                    ? ` (${t('adminBilling.assignPlan.onQuote')})`
                    : ` — ${Number(p.base_price).toFixed(2)} €/pack`}
                </option>
              ))}
            </select>
          </div>
          {selectedPlan && !selectedPlan.is_custom && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('adminBilling.assignPlan.packsLabel')}
              </label>
              <input
                type="number"
                min={1}
                step={1}
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {t('adminBilling.assignPlan.monthlyTotal')}{' '}
                <strong>{(Number(selectedPlan.base_price) * quantity).toFixed(2)} €</strong>
              </p>
            </div>
          )}
          {selectedPlan && (
            <div className="bg-gray-50 dark:bg-gray-900/40 rounded-lg p-3">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
                {t('adminBilling.assignPlan.effectiveQuotas')}
              </p>
              <QuotaGrid plan={selectedPlan} qty={quantity} custom={selectedPlan.is_custom} />
            </div>
          )}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-700 rounded-lg"
          >
            {t('adminBilling.assignPlan.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
          >
            {saving ? (
              <RefreshCw className="w-4 h-4 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4" />
            )}{' '}
            {t('adminBilling.assignPlan.activate')}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

type Tab = 'plans' | 'erps' | 'costs'

export default function AdminBilling() {
  const { t, i18n } = useTranslation()
  const [tab, setTab] = useState<Tab>('plans')

  const [plans, setPlans] = useState<SubscriptionPlan[]>([])
  const [plansLoading, setPlansLoading] = useState(true)
  const [editingPlan, setEditingPlan] = useState<Partial<SubscriptionPlan> | null | false>(false)

  const [dashboard, setDashboard] = useState<{
    summary: Record<string, number>
    erps: ErpBillingRow[]
  } | null>(null)
  const [dashLoading, setDashLoading] = useState(false)
  const [expandedErp, setExpandedErp] = useState<number | null>(null)
  const [addCreditsErp, setAddCreditsErp] = useState<ErpBillingRow | null>(null)
  const [assignPlanErp, setAssignPlanErp] = useState<ErpBillingRow | null>(null)

  const [costs, setCosts] = useState<CreditCost[]>([])
  const [costsLoading, setCostsLoading] = useState(false)
  const [costsDirty, setCostsDirty] = useState(false)
  const [costsSaving, setCostsSaving] = useState(false)
  const [costsMsg, setCostsMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const loadPlans = async () => {
    setPlansLoading(true)
    try {
      const r = await api.getSubscriptionPlans()
      setPlans(r.plans ?? [])
    } catch {
      /* silently */
    } finally {
      setPlansLoading(false)
    }
  }

  const loadDashboard = async () => {
    setDashLoading(true)
    try {
      const r = await api.getBillingDashboard()
      setDashboard({ summary: r.summary ?? {}, erps: r.erps ?? [] })
    } catch {
      /* silently */
    } finally {
      setDashLoading(false)
    }
  }

  const loadCosts = async () => {
    setCostsLoading(true)
    try {
      const r = await api.getCreditCosts()
      setCosts(r.costs ?? [])
    } catch {
      /* silently */
    } finally {
      setCostsLoading(false)
    }
  }

  useEffect(() => {
    loadPlans()
  }, [])
  useEffect(() => {
    if (tab === 'erps' && !dashboard) loadDashboard()
    if (tab === 'costs' && costs.length === 0) loadCosts()
  }, [tab]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleDeletePlan = async (plan: SubscriptionPlan) => {
    if (!confirm(t('adminBilling.plansTab.confirmDeactivate', { name: plan.name }))) return
    try {
      await api.deleteSubscriptionPlan(plan.id)
      loadPlans()
    } catch {
      alert(t('adminBilling.plansTab.deactivateError'))
    }
  }

  const handleSaveCosts = async () => {
    setCostsSaving(true)
    setCostsMsg(null)
    try {
      const r = await api.saveCreditCosts(costs)
      setCostsMsg({ type: r.success ? 'success' : 'error', text: r.message })
      setCostsDirty(false)
    } catch (e: unknown) {
      setCostsMsg({ type: 'error', text: (e as Error)?.message ?? 'Error.' })
    } finally {
      setCostsSaving(false)
    }
  }

  const updateCost = (
    idx: number,
    field: keyof CreditCost,
    value: CreditCost[keyof CreditCost]
  ) => {
    setCosts((prev) => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)))
    setCostsDirty(true)
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('adminBilling.title')}
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          {t('adminBilling.subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700">
        {(
          [
            { key: 'plans', label: t('adminBilling.tabs.plans'), icon: Package },
            { key: 'erps', label: t('adminBilling.tabs.erps'), icon: CreditCard },
            { key: 'costs', label: t('adminBilling.tabs.costs'), icon: Cpu },
          ] as { key: Tab; label: string; icon: LucideIcon }[]
        ).map(({ key, label, icon: Icon }) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
              tab === key
                ? 'border-purple-600 text-purple-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {/* ── Plans ── */}
      {tab === 'plans' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={() => setEditingPlan({})}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" /> {t('adminBilling.plansTab.newPlan')}
            </button>
          </div>
          {plansLoading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-5 space-y-3 flex flex-col"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {plan.name}
                      </h3>
                      {
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {' '}
                          {(i18n.language.startsWith('en')
                            ? plan.description_en
                            : plan.description_fr) || plan.description}
                        </p>
                      }
                    </div>
                    <StatusBadge active={plan.is_active} />
                  </div>
                  {plan.is_custom ? (
                    <p className="text-2xl font-bold text-purple-600">
                      {t('adminBilling.plansTab.onQuote')}
                    </p>
                  ) : (
                    <p className="text-2xl font-bold text-purple-600">
                      {Number(plan.base_price).toFixed(2)} €
                      <span className="text-sm font-normal text-gray-400">
                        {t('adminBilling.plansTab.perPackPerMonth')}
                      </span>
                    </p>
                  )}
                  <div className="flex-1">
                    <QuotaGrid plan={plan} custom={plan.is_custom} />
                  </div>
                  {plan.features?.length > 0 && (
                    <ul className="space-y-0.5 pt-2 border-t border-gray-100 dark:border-gray-700">
                      {plan.features.map((f, i) => (
                        <li
                          key={i}
                          className="flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400"
                        >
                          <CheckCircle2 className="w-3 h-3 text-green-500 flex-shrink-0" /> {f}
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <button
                      onClick={() => setEditingPlan(plan)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <Pencil className="w-3.5 h-3.5" /> {t('adminBilling.plansTab.edit')}
                    </button>
                    <button
                      onClick={() => handleDeletePlan(plan)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-red-600 border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> {t('adminBilling.plansTab.deactivate')}
                    </button>
                  </div>
                </div>
              ))}
              {plans.length === 0 && (
                <p className="col-span-4 text-center py-12 text-gray-400">
                  {t('adminBilling.plansTab.noPlans')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── ERP Subscriptions ── */}
      {tab === 'erps' && (
        <div className="space-y-4">
          <div className="flex justify-end">
            <button
              onClick={loadDashboard}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
            >
              <RefreshCw className={`w-4 h-4 ${dashLoading ? 'animate-spin' : ''}`} />{' '}
              {t('adminBilling.erpsTab.refresh')}
            </button>
          </div>
          {dashboard && (
            <div className="grid grid-cols-3 gap-4">
              {[
                {
                  labelKey: 'erpsTab.totalErps',
                  value: dashboard.summary?.total_erps ?? 0,
                  fmt: (v: number) => String(v),
                  color: 'text-gray-900 dark:text-white',
                },
                {
                  labelKey: 'erpsTab.availableCredits',
                  value: dashboard.summary?.total_available_credits ?? 0,
                  fmt: (v: number) => v.toLocaleString('fr-FR') + ' crédits',
                  color: 'text-purple-600',
                },
                {
                  labelKey: 'erpsTab.monthlyValue',
                  value: dashboard.summary?.total_subscription_value ?? 0,
                  fmt: (v: number) => (v / 1000).toFixed(0) + ' €',
                  color: 'text-green-600',
                },
              ].map(({ labelKey, value, fmt: f, color }) => (
                <div
                  key={labelKey}
                  className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 text-center"
                >
                  <p className={`text-2xl font-bold ${color}`}>{f(value)}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    {t(`adminBilling.${labelKey}`)}
                  </p>
                </div>
              ))}
            </div>
          )}
          {dashLoading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
              {(dashboard?.erps ?? []).map((erp) => {
                const isExpanded = expandedErp === erp.erp_id
                const sub = erp.subscription
                return (
                  <div
                    key={erp.erp_id}
                    className="border-b border-gray-100 dark:border-gray-700 last:border-0"
                  >
                    <div
                      className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-750"
                      onClick={() => setExpandedErp(isExpanded ? null : erp.erp_id)}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        )}
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">
                            {erp.erp_name}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">{erp.erp_type}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 flex-shrink-0">
                        <span className="text-sm text-gray-500 dark:text-gray-400">
                          {sub?.plan_name ?? '—'}
                          {sub && !sub.is_custom && sub.quantity > 1 && (
                            <span className="ml-1 text-purple-500">×{sub.quantity}</span>
                          )}
                        </span>
                        <StatusBadge active={sub?.is_active ?? false} />
                        <span className="text-sm font-semibold text-purple-600">
                          {(sub?.credit_balance ?? 0).toLocaleString('fr-FR')} cr.
                        </span>
                      </div>
                    </div>
                    {isExpanded && sub && (
                      <div className="px-5 pb-5 bg-gray-50 dark:bg-gray-900/30 space-y-4">
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          <div>
                            <p className="text-gray-500 text-xs mb-0.5">
                              {t('adminBilling.erpsTab.statusLabel')}
                            </p>
                            <p className="font-medium capitalize">{sub.status}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs mb-0.5">
                              {t('adminBilling.erpsTab.nextBilling')}
                            </p>
                            <p className="font-medium">{sub.next_billing_date ?? '—'}</p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs mb-0.5">
                              {t('adminBilling.erpsTab.creditBalance')}
                            </p>
                            <p className="font-medium text-purple-600">
                              {(sub.credit_balance ?? 0).toLocaleString('fr-FR')} crédits
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500 text-xs mb-0.5">
                              {t('adminBilling.erpsTab.purchasedPacks')}
                            </p>
                            <p className="font-medium">
                              {sub.is_custom ? t('adminBilling.erpsTab.customValue') : sub.quantity}
                            </p>
                          </div>
                        </div>
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">
                            {t('adminBilling.erpsTab.effectiveQuotas')}
                          </p>
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
                            {[
                              {
                                icon: Users,
                                labelKey: 'quota.users',
                                value: sub.effective_max_users,
                              },
                              {
                                icon: Building2,
                                labelKey: 'quota.clients',
                                value: sub.effective_max_clients,
                              },
                              {
                                icon: FileSignature,
                                labelKey: 'quota.signatures',
                                value: sub.effective_max_signatures,
                              },
                              {
                                icon: Cpu,
                                labelKey: 'quota.aiTokens',
                                value: sub.effective_max_ai_tokens,
                              },
                              {
                                icon: Mail,
                                labelKey: 'quota.emails',
                                value: sub.effective_max_emails,
                              },
                            ].map(({ icon: Icon, labelKey, value }) => (
                              <div key={labelKey} className="flex items-center gap-1.5">
                                <Icon className="w-3.5 h-3.5 text-gray-400" />
                                <div>
                                  <p className="text-xs text-gray-400">
                                    {t(`adminBilling.${labelKey}`)}
                                  </p>
                                  <p className="font-medium text-gray-900 dark:text-white">
                                    {sub.is_custom
                                      ? t('adminBilling.erpsTab.customValue')
                                      : fmt(value)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setAssignPlanErp(erp)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                          >
                            <Package className="w-3.5 h-3.5" />{' '}
                            {t('adminBilling.erpsTab.changePlan')}
                          </button>
                          <button
                            onClick={() => setAddCreditsErp(erp)}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-xs border border-purple-300 text-purple-700 rounded-lg hover:bg-purple-50"
                          >
                            <PlusCircle className="w-3.5 h-3.5" />{' '}
                            {t('adminBilling.erpsTab.addCredits')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
              {(dashboard?.erps ?? []).length === 0 && !dashLoading && (
                <p className="text-center py-12 text-gray-400">
                  {t('adminBilling.erpsTab.noErps')}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Credit Costs ── */}
      {tab === 'costs' && (
        <div className="space-y-4">
          <p
            className="text-sm text-gray-500 dark:text-gray-400"
            dangerouslySetInnerHTML={{ __html: t('adminBilling.costsTab.description') }}
          />
          {costsMsg && (
            <div
              className={`flex items-center gap-2 p-3 rounded-lg text-sm ${costsMsg.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}
            >
              {costsMsg.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
              )}
              {costsMsg.text}
            </div>
          )}
          {costsLoading ? (
            <div className="flex justify-center py-12">
              <RefreshCw className="w-6 h-6 text-gray-400 animate-spin" />
            </div>
          ) : (
            <>
              {/* Mobile card layout */}
              <ul className="divide-y divide-gray-100 dark:divide-gray-700 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl sm:hidden">
                {costs.map((cost, idx) => (
                  <li key={cost.resource_type} className="p-4 space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 dark:text-white text-sm">
                          {t(`adminBilling.resources.${cost.resource_type}`, {
                            defaultValue: cost.resource_type,
                          })}
                        </p>
                        {cost.description && (
                          <p className="text-xs text-gray-400 mt-0.5">{cost.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <label className="text-xs text-gray-500 dark:text-gray-400">
                          {t('adminBilling.costsTab.colActive')}
                        </label>
                        <input
                          type="checkbox"
                          checked={!!cost.is_active}
                          onChange={(e) => updateCost(idx, 'is_active', e.target.checked)}
                          className="w-4 h-4 accent-purple-600"
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('adminBilling.costsTab.colCreditsPerUnit')}
                        </label>
                        <input
                          type="number"
                          min={0}
                          step={1}
                          value={cost.credits_per_unit}
                          onChange={(e) =>
                            updateCost(idx, 'credits_per_unit', parseInt(e.target.value) || 0)
                          }
                          className="w-full text-right px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">
                          {t('adminBilling.costsTab.colUnitsPerBilling')}
                        </label>
                        <input
                          type="number"
                          min={1}
                          step={1}
                          value={cost.units_per_credit}
                          onChange={(e) =>
                            updateCost(idx, 'units_per_credit', parseInt(e.target.value) || 1)
                          }
                          className="w-full text-right px-2 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </li>
                ))}
                {costs.length === 0 && (
                  <li className="p-6 text-center text-gray-400 text-sm">
                    {t('adminBilling.costsTab.noCosts')}
                  </li>
                )}
              </ul>
              {/* Desktop table layout */}
              <div className="hidden sm:block bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                      {[
                        t('adminBilling.costsTab.colResource'),
                        t('adminBilling.costsTab.colCreditsPerUnit'),
                        t('adminBilling.costsTab.colUnitsPerBilling'),
                        t('adminBilling.costsTab.colActive'),
                      ].map((h, i) => (
                        <th
                          key={h}
                          className={`px-5 py-3 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider ${i === 0 ? 'text-left' : i === 3 ? 'text-center' : 'text-right'}`}
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                    {costs.map((cost, idx) => (
                      <tr
                        key={cost.resource_type}
                        className="hover:bg-gray-50 dark:hover:bg-gray-750"
                      >
                        <td className="px-5 py-3 font-medium text-gray-900 dark:text-white">
                          {t(`adminBilling.resources.${cost.resource_type}`, {
                            defaultValue: cost.resource_type,
                          })}
                          <p className="text-xs text-gray-400 font-normal">{cost.description}</p>
                        </td>
                        <td className="px-5 py-3 text-right">
                          <input
                            type="number"
                            min={0}
                            step={1}
                            value={cost.credits_per_unit}
                            onChange={(e) =>
                              updateCost(idx, 'credits_per_unit', parseInt(e.target.value) || 0)
                            }
                            className="w-24 text-right px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-5 py-3 text-right">
                          <input
                            type="number"
                            min={1}
                            step={1}
                            value={cost.units_per_credit}
                            onChange={(e) =>
                              updateCost(idx, 'units_per_credit', parseInt(e.target.value) || 1)
                            }
                            className="w-24 text-right px-2 py-1 border border-gray-300 dark:border-gray-600 rounded-lg text-sm dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                          />
                        </td>
                        <td className="px-5 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!cost.is_active}
                            onChange={(e) => updateCost(idx, 'is_active', e.target.checked)}
                            className="w-4 h-4 accent-purple-600"
                          />
                        </td>
                      </tr>
                    ))}
                    {costs.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center py-12 text-gray-400">
                          {t('adminBilling.costsTab.noCosts')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              {costsDirty && (
                <div className="flex justify-end">
                  <button
                    onClick={handleSaveCosts}
                    disabled={costsSaving}
                    className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 disabled:opacity-50"
                  >
                    {costsSaving ? (
                      <RefreshCw className="w-4 h-4 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4" />
                    )}
                    {t('adminBilling.costsTab.saveCosts')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Modals */}
      {editingPlan !== false && (
        <PlanForm
          plan={editingPlan}
          onClose={() => setEditingPlan(false)}
          onSaved={() => {
            setEditingPlan(false)
            loadPlans()
          }}
        />
      )}
      {addCreditsErp && (
        <AddCreditsModal
          erp={addCreditsErp}
          onClose={() => setAddCreditsErp(null)}
          onAdded={() => {
            setAddCreditsErp(null)
            loadDashboard()
          }}
        />
      )}
      {assignPlanErp && (
        <AssignPlanModal
          erp={assignPlanErp}
          plans={plans.filter((p) => p.is_active)}
          onClose={() => setAssignPlanErp(null)}
          onAssigned={() => {
            setAssignPlanErp(null)
            loadDashboard()
          }}
        />
      )}
    </div>
  )
}
